import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import FeatherIcon from 'feather-icons-react';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import {
  fetchAssets,
  deleteAsset,
  editAsset,
  AssetDataType,
  clearError,
  UpdateAssetPayload
} from 'store/assetsSlice';
import AssetActionDropdownItems from 'components/common/AssetActionDropdownItems';
import EditAssetModal from 'components/modals/EditAssetModal';
import RemoveAssetModal from 'components/modals/RemoveAssetModal';
import ViewAssetModal from 'components/modals/ViewAssetModal';
import ReturnAssetModal from 'components/modals/ReturnAssetModal';
import ReassignAssetModal from 'components/modals/ReassignAssetModal';
import MaintenanceAssetModal from 'components/modals/MaintenanceAssetModal';
import RetireAssetModal from 'components/modals/RetireAssetModal';
import PhoenixLoader from 'components/common/PhoenixLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLaptop,
  faBuilding,
  faTag,
  faChartBar,
  faWrench,
  faUser,
  faCog
} from '@fortawesome/free-solid-svg-icons';

interface AssetTableProps {
  searchQuery?: string;
  selectedCompanyId?: number | null;
  selectedStatus?: string | null;
  selectedCondition?: string | null;
}

// ✅ FIXED: Use DisplayAsset for all handlers
type AssetActionHandler = (asset: DisplayAsset) => void;

// Extended interface for display purposes
interface DisplayAsset extends AssetDataType {
  deviceName: string;
  serialNumber: string;
  category: string;
  assignedTo?: string;
}

const AssetTable = ({
  searchQuery = '',
  selectedCompanyId = null,
  selectedStatus = null,
  selectedCondition = null
}: AssetTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, loading, error } = useSelector(
    (state: RootState) => state.assets
  );
  const { companyId } = useSelector((state: RootState) => state.auth);

  const [selectedAsset, setSelectedAsset] = useState<DisplayAsset | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<DisplayAsset | null>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [viewAsset, setViewAsset] = useState<DisplayAsset | null>(null);
  const [assetToReturn, setAssetToReturn] = useState<DisplayAsset | null>(null);
  const [openReturnModal, setOpenReturnModal] = useState(false);
  const [assetToReassign, setAssetToReassign] = useState<DisplayAsset | null>(
    null
  );
  const [openReassignModal, setOpenReassignModal] = useState(false);
  const [assetForMaintenance, setAssetForMaintenance] =
    useState<DisplayAsset | null>(null);
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState<'start' | 'complete'>(
    'start'
  );
  const [assetToRetire, setAssetToRetire] = useState<DisplayAsset | null>(null);
  const [openRetireModal, setOpenRetireModal] = useState(false);
  const [retireMode, setRetireMode] = useState<'retire' | 'reactivate'>(
    'retire'
  );

  useEffect(() => {
    dispatch(fetchAssets());
  }, [dispatch]);

  // Clear error when component unmounts or on retry
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // ✅ FIXED: Transform backend data to frontend format with proper typing
  const transformedData = useMemo((): DisplayAsset[] => {
    return data.map(asset => ({
      ...asset,
      // Map backend fields to frontend display fields
      deviceName: asset.name,
      serialNumber: asset.serial_number,
      category: asset.category_name || 'Uncategorized',
      assignedTo: asset.current_holder_name // Remove the fallback to 'Unassigned'
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    let filtered = transformedData;

    // Filter by company_id if selectedCompanyId is not null
    if (selectedCompanyId !== null && selectedCompanyId !== undefined) {
      filtered = filtered.filter(
        asset => asset.company_id === selectedCompanyId
      );
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(
        asset => asset.status.toLowerCase() === selectedStatus.toLowerCase()
      );
    }

    // Filter by condition
    if (selectedCondition) {
      filtered = filtered.filter(
        asset =>
          asset.condition.toLowerCase() === selectedCondition.toLowerCase()
      );
    }

    // Filter by search query
    if (!searchQuery.trim()) return filtered;
    const query = searchQuery.toLowerCase().trim();
    return filtered.filter(
      asset =>
        asset.deviceName.toLowerCase().includes(query) ||
        asset.category.toLowerCase().includes(query) ||
        asset.brand.toLowerCase().includes(query) ||
        asset.model.toLowerCase().includes(query) ||
        (asset.company_name &&
          asset.company_name.toLowerCase().includes(query)) ||
        (asset.assignedTo && asset.assignedTo.toLowerCase().includes(query))
    );
  }, [
    transformedData,
    searchQuery,
    selectedCompanyId,
    selectedStatus,
    selectedCondition
  ]);

  // ✅ FIXED: All handlers now use DisplayAsset
  const handleDelete: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetToDelete(asset);
    setOpenDeleteModal(true);
  };

  // ✅ FIXED: Update confirmDelete to accept number (asset ID)
  const confirmDelete = async (id: number) => {
    try {
      await dispatch(deleteAsset(id)).unwrap();
      setOpenDeleteModal(false);
      setAssetToDelete(null);
      dispatch(fetchAssets()); // Refresh the list after deletion
    } catch (error) {
      console.error('Failed to delete asset:', error);
      dispatch(fetchAssets()); // Refresh anyway to sync state
    }
  };

  const handleEdit: AssetActionHandler = (asset: DisplayAsset) => {
    setSelectedAsset(asset);
    setOpenEditModal(true);
  };

  const handleReturn: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetToReturn(asset);
    setOpenReturnModal(true);
  };

  const handleReturnSuccess = () => {
    setOpenReturnModal(false);
    setAssetToReturn(null);
    dispatch(fetchAssets()); // Refresh the list after return
  };

  const handleReassign: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetToReassign(asset);
    setOpenReassignModal(true);
  };

  const handleReassignSuccess = () => {
    setOpenReassignModal(false);
    setAssetToReassign(null);
    dispatch(fetchAssets()); // Refresh the list after reassignment
  };

  const handlePutMaintenance: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetForMaintenance(asset);
    setMaintenanceMode('start');
    setOpenMaintenanceModal(true);
  };

  const handleCompleteMaintenance: AssetActionHandler = (
    asset: DisplayAsset
  ) => {
    setAssetForMaintenance(asset);
    setMaintenanceMode('complete');
    setOpenMaintenanceModal(true);
  };

  const handleMaintenanceSuccess = () => {
    setOpenMaintenanceModal(false);
    setAssetForMaintenance(null);
    dispatch(fetchAssets()); // Refresh the list after maintenance update
  };

  const handleRetire: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetToRetire(asset);
    setRetireMode('retire');
    setOpenRetireModal(true);
  };

  const handleReactivate: AssetActionHandler = (asset: DisplayAsset) => {
    setAssetToRetire(asset);
    setRetireMode('reactivate');
    setOpenRetireModal(true);
  };

  const handleRetireSuccess = () => {
    setOpenRetireModal(false);
    setAssetToRetire(null);
    dispatch(fetchAssets()); // Refresh the list after retire/reactivate
  };

  const handleSaveEdit = async (updatedAsset: DisplayAsset) => {
    try {
      // Convert to UpdateAssetRequest format for the API
      const updateData: UpdateAssetPayload = {
        assetId: updatedAsset.id,
        assetData: {
          name: updatedAsset.name,
          asset_tag: updatedAsset.asset_tag,
          serial_number: updatedAsset.serial_number,
          brand: updatedAsset.brand,
          model: updatedAsset.model,
          description: updatedAsset.description,
          status: updatedAsset.status,
          condition: updatedAsset.condition,
          purchase_date: updatedAsset.purchase_date,
          purchase_cost: updatedAsset.purchase_cost,
          purchase_invoice_number: updatedAsset.purchase_invoice_number,
          vendor: updatedAsset.vendor,
          warranty_expiry_date: updatedAsset.warranty_expiry_date,
          location: updatedAsset.location,
          current_holder_id: updatedAsset.current_holder_id
        }
      };

      await dispatch(editAsset(updateData)).unwrap();
      setOpenEditModal(false);
      setSelectedAsset(null);
      dispatch(fetchAssets());
    } catch (error) {
      console.error('Failed to update asset:', error);
    }
  };

  const handleView: AssetActionHandler = (asset: DisplayAsset) => {
    setViewAsset(asset);
  };

  const handleRetry = () => {
    dispatch(clearError());
    dispatch(fetchAssets());
  };

  const table = useAdvanceTable({
    data: filteredData,
    columns: getColumns(
      handleEdit,
      handleDelete,
      handleView,
      handleReturn,
      handleReassign,
      handlePutMaintenance,
      handleCompleteMaintenance,
      handleRetire,
      handleReactivate
    ),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {loading ? (
          <PhoenixLoader />
        ) : error ? (
          <p className="p-4 text-danger">Error: {error}</p>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {openEditModal && selectedAsset && (
        <EditAssetModal
          show={openEditModal}
          handleClose={() => {
            setOpenEditModal(false);
            setSelectedAsset(null);
          }}
          asset={selectedAsset}
          onSave={handleSaveEdit}
        />
      )}

      {openDeleteModal && assetToDelete && (
        <RemoveAssetModal
          show={openDeleteModal}
          handleClose={() => {
            setOpenDeleteModal(false);
            setAssetToDelete(null);
          }}
          asset={assetToDelete}
          onDelete={confirmDelete} // Now this passes the asset ID (number)
        />
      )}

      {viewAsset && (
        <ViewAssetModal
          show={!!viewAsset}
          handleClose={() => setViewAsset(null)}
          asset={viewAsset}
        />
      )}

      {openReturnModal && assetToReturn && (
        <ReturnAssetModal
          show={openReturnModal}
          handleClose={() => {
            setOpenReturnModal(false);
            setAssetToReturn(null);
          }}
          asset={assetToReturn}
          onReturnSuccess={handleReturnSuccess}
        />
      )}

      {openReassignModal && assetToReassign && (
        <ReassignAssetModal
          show={openReassignModal}
          handleClose={() => {
            setOpenReassignModal(false);
            setAssetToReassign(null);
          }}
          asset={assetToReassign}
          onReassignSuccess={handleReassignSuccess}
        />
      )}

      {openMaintenanceModal && assetForMaintenance && (
        <MaintenanceAssetModal
          show={openMaintenanceModal}
          handleClose={() => {
            setOpenMaintenanceModal(false);
            setAssetForMaintenance(null);
          }}
          asset={assetForMaintenance}
          onMaintenanceSuccess={handleMaintenanceSuccess}
          mode={maintenanceMode}
        />
      )}

      {openRetireModal && assetToRetire && (
        <RetireAssetModal
          show={openRetireModal}
          handleClose={() => {
            setOpenRetireModal(false);
            setAssetToRetire(null);
          }}
          asset={assetToRetire}
          onRetireSuccess={handleRetireSuccess}
          mode={retireMode}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default AssetTable;

// Column definitions with proper typing
function getColumns(
  onEdit: AssetActionHandler,
  onDelete: AssetActionHandler,
  onView: AssetActionHandler,
  onReturn: AssetActionHandler,
  onReassign: AssetActionHandler,
  onPutMaintenance: AssetActionHandler,
  onCompleteMaintenance: AssetActionHandler,
  onRetire: AssetActionHandler,
  onReactivate: AssetActionHandler
): ColumnDef<DisplayAsset>[] {
  return [
    {
      id: 'sn',
      header: 'SN',
      cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
      meta: {
        headerProps: {
          style: { width: '80px' }
        },
        cellProps: {}
      }
    },
    {
      accessorKey: 'deviceName',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faLaptop} className="me-2 text-primary" />
          Asset Name
        </span>
      ),
      cell: ({ row: { original } }) => (
        <button
          className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
          onClick={() => onView(original)}
        >
          {original.deviceName}
        </button>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'company_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
          Company
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.company_name || 'N/A'}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'category',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
          Category
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.category}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    // {
    //   accessorKey: 'status',
    //   header: 'Status',
    //   cell: ({ row: { original } }) => {
    //     const statusColors: Record<string, string> = {
    //       available: 'bg-success',
    //       assigned: 'bg-primary',
    //       maintenance: 'bg-warning',
    //       retired: 'bg-danger'
    //     };

    //     return (
    //       <span
    //         className={`badge ${
    //           statusColors[original.status] || 'bg-secondary'
    //         }`}
    //       >
    //         {original.status.charAt(0).toUpperCase() + original.status.slice(1)}
    //       </span>
    //     );
    //   },
    //   meta: {
    //     headerProps: {
    //       className: 'ps-2 bg-white py-3 text-uppercase'
    //     },
    //     cellProps: { className: 'ps-2' }
    //   }
    // },
    // {
    //   accessorKey: 'condition',
    //   header: 'Condition',
    //   cell: ({ row: { original } }) => {
    //     const conditionColors: Record<string, string> = {
    //       excellent: 'bg-success',
    //       good: 'bg-info',
    //       fair: 'bg-warning',
    //       poor: 'bg-danger'
    //     };

    //     return (
    //       <span
    //         className={`badge ${
    //           conditionColors[original.condition] || 'bg-secondary'
    //         }`}
    //       >
    //         {original.condition.charAt(0).toUpperCase() +
    //           original.condition.slice(1)}
    //       </span>
    //     );
    //   },
    //   meta: {
    //     headerProps: {
    //       className: 'ps-2 bg-white py-3 text-uppercase'
    //     },
    //     cellProps: { className: 'ps-2' }
    //   }
    // },
    {
      accessorKey: 'status',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faChartBar} className="me-2 text-primary" />
          Status
        </span>
      ),
      cell: ({ row: { original } }) => {
        const statusConfig: Record<
          string,
          { className: string; icon: string }
        > = {
          available: {
            className: 'badge-phoenix-success',
            icon: 'check'
          },
          assigned: {
            className: 'badge-phoenix-primary',
            icon: 'package'
          },
          maintenance: {
            className: 'badge-phoenix-warning',
            icon: 'alert-octagon'
          },
          retired: {
            className: 'badge-phoenix-danger',
            icon: 'x'
          }
        };

        const config = statusConfig[original.status] || {
          className: 'badge-phoenix-secondary',
          icon: 'info'
        };

        const label =
          original.status.charAt(0).toUpperCase() + original.status.slice(1);

        return (
          <span className={`badge badge-phoenix fs-10 ${config.className}`}>
            <span className="badge-label">{label}</span>
            <FeatherIcon icon={config.icon} size={12} className="ms-1" />
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },

    {
      accessorKey: 'condition',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faWrench} className="me-2 text-primary" />
          Condition
        </span>
      ),
      cell: ({ row: { original } }) => {
        const conditionConfig: Record<
          string,
          { className: string; icon: string }
        > = {
          excellent: {
            className: 'badge-phoenix-success',
            icon: 'award'
          },
          good: {
            className: 'badge-phoenix-info',
            icon: 'thumbs-up'
          },
          fair: {
            className: 'badge-phoenix-warning',
            icon: 'alert-triangle'
          },
          poor: {
            className: 'badge-phoenix-danger',
            icon: 'thumbs-down'
          }
        };

        const config = conditionConfig[original.condition] || {
          className: 'badge-phoenix-secondary',
          icon: 'help-circle'
        };

        const label =
          original.condition.charAt(0).toUpperCase() +
          original.condition.slice(1);

        return (
          <span className={`badge badge-phoenix fs-10 ${config.className}`}>
            <span className="badge-label">{label}</span>
            <FeatherIcon icon={config.icon} size={12} className="ms-1" />
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },

    {
      accessorKey: 'assignedTo',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
          Assigned To
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className={original.assignedTo ? 'text-body' : 'text-muted'}>
          {original.assignedTo || 'Unassigned'}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'actions',
      header: () => (
        <div className="d-inline-flex align-items-center">
          <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
          Actions
        </div>
      ),
      cell: ({ row: { original } }) => (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <AssetActionDropdownItems
              onView={() => onView(original)}
              onEdit={() => onEdit(original)}
              onRemove={() => onDelete(original)}
              onReturn={() => onReturn(original)}
              onAssign={() => onReassign(original)}
              onPutMaintenance={() => onPutMaintenance(original)}
              onCompleteMaintenance={() => onCompleteMaintenance(original)}
              onRetire={() => onRetire(original)}
              onReactivate={() => onReactivate(original)}
              assetStatus={original.status}
              isAssigned={original.status === 'assigned'}
            />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: {
          className: 'text-end pe-3'
        },
        cellProps: { className: 'text-end pe-3' }
      }
    }
  ];
}
